'use client';

import { apolloClientSideClient } from '@/apollo/apolloClient';
import { makeStore } from '@/state/store';
import useInitDataDog from '@/util/datadog';
import { ApolloProvider } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
// import { datadogRum } from '@datadog/browser-rum';
import prismatic from '@prismatic-io/embedded';
// import { withLDProvider } from 'launchdarkly-react-client-sdk';
// import type { AppProps } from 'next/app';
import BannerContainer from '@/app/components/banners/BannerContainer';
import ErrorBoundary from '@/app/components/errors/ErrorBoundary';
import { EditContextProvider } from '@/app/components/providers/edit-context';
import ImpersonationContextProvider from '@/app/components/providers/ImpersonationContext';
import ModalProvider from '@/app/components/providers/Modal';
import ObjectCardScrollProvider from '@/app/components/providers/ObjectCardScroll.context';
import { useLoggedInUser } from '@/hooks/useLoggedInUser';
import { env } from '@/util/env.utils';
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google';
import { type PropsWithChildren, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Slide, ToastContainer } from 'react-toastify';

interface AppProps {
  children: React.ReactNode;
}

const reduxStore = makeStore();

export default function App ({ children }: Readonly<AppProps>): JSX.Element {
  useEffect(() => {
    datadogLogs.logger.info('Client Application Started');
    prismatic.init();
  }, []);

  return (
    <>
      <GoogleAnalytics gaId={env('NEXT_PUBLIC_GOOGLE_ANALYTICS_ID')} />
      <GoogleTagManager gtmId={env('NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID')} />
      <ErrorBoundary>
        <ApolloProvider client={apolloClientSideClient}>
          <AppWithApollo>
            {children}
          </AppWithApollo>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  );
}

function AppWithApollo ({ children }: PropsWithChildren): JSX.Element {
  const loggedInUser = useLoggedInUser();
  useInitDataDog({ user: loggedInUser });

  return (
    <ReduxProvider store={reduxStore}>
      <EditContextProvider canUserEdit={true}>
        <ObjectCardScrollProvider>
          <ModalProvider>
            <ImpersonationContextProvider>
              <BannerContainer />
              <div className='flex overflow-scroll w-screen h-screen bg-zinc-50'>
                {children}
              </div>
              <ToastContainer
                data-headlessui-portal
                transition={Slide}
                closeButton={false}
                pauseOnFocusLoss={false}
                className='w-[370px]'
              />
            </ImpersonationContextProvider>
          </ModalProvider>
        </ObjectCardScrollProvider>
      </EditContextProvider>
    </ReduxProvider>
  );
}

// export default withLDProvider({
//   clientSideID: process.env.NEXT_PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID,
//   options: {
//     inspectors: [
//       {
//         type: 'flag-used',
//         name: 'dd-inspector',
//         method: (key, detail) => {
//           datadogRum.addFeatureFlagEvaluation(key, detail.value);
//         },
//       },
//     ],
//   },
// })(App);
